import { useEffect, useState } from "react";
import { Bell, ExternalLink, Mail, MessageSquare, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function channelIcon(c) {
  if (c === "email") return <Mail size={11} />;
  if (c === "popup") return <AlertCircle size={11} />;
  return <MessageSquare size={11} />;
}

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/aff-panel/announcements/inbox");
      setItems(r.data?.items || []);
      setUnread(r.data?.unread || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load announcements");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (a) => {
    if (a.read) return;
    try { await api.post(`/aff-panel/announcements/${a.announcement_id}/read`); await load(); } catch (_) { /* ignore */ }
  };

  return (
    <div data-testid="announcements-page">
      <PageHeader
        eyebrow="ANNOUNCEMENTS"
        title="Affiliate Announcements"
        subtitle="All admin messages — bonus campaigns, policy updates, and big news."
        right={(
          <div className="text-[11px] uppercase tracking-[0.22em] text-emerald-200 font-bold">
            {unread} UNREAD
          </div>
        )}
      />

      {loading ? (
        <div className="p-glass p-10 text-center text-white/55">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-glass p-10 text-center">
          <Bell size={28} className="mx-auto text-emerald-200 mb-2" />
          <div className="text-white font-bold">No announcements yet</div>
          <div className="text-white/55 text-sm">Stay tuned — we'll let you know when something important happens.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div
              key={a.id}
              className="p-glass p-4 transition cursor-pointer"
              onClick={() => markRead(a)}
              style={{ borderLeft: a.read ? "1px solid rgba(255,255,255,0.08)" : "3px solid #d4af37" }}
              data-testid={`announcement-${a.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="shrink-0 mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ background: "rgba(212,175,55,0.1)", border: "1px solid rgba(212,175,55,0.35)", color: "#d4af37" }}>
                  <Bell size={15} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-black text-white text-[15px]">{a.title}</h4>
                    {!a.read && (
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-[0.2em]"
                            style={{ color: "#fde68a", background: "rgba(212,175,55,0.16)", border: "1px solid rgba(212,175,55,0.4)" }}>
                        NEW
                      </span>
                    )}
                    <span className="text-[10.5px] text-white/40 ml-auto">{timeAgo(a.created_at)}</span>
                  </div>
                  <div className="text-white/80 text-[13.5px] mt-1 whitespace-pre-wrap">{a.body}</div>
                  <div className="flex items-center gap-3 mt-2">
                    <div className="flex items-center gap-1.5">
                      {(a.channels || []).map((c) => (
                        <span key={c} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-[0.18em] text-white/55"
                              style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
                          {channelIcon(c)} {c}
                        </span>
                      ))}
                    </div>
                    {a.cta_label && a.cta_url ? (
                      <a href={a.cta_url} target="_blank" rel="noreferrer"
                         onClick={(e) => e.stopPropagation()}
                         className="ml-auto inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200 text-[12px] font-bold"
                         data-testid={`cta-${a.id}`}>
                        {a.cta_label} <ExternalLink size={11} />
                      </a>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
